import {
  AbsoluteCenter,
  ActionBar,
  Box,
  Center,
  Checkbox,
  EmptyState,
  HStack,
  Icon,
  IconButton,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  type GroupingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { t } from 'i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FaCheck, FaLongArrowAltRight, FaTimes } from 'react-icons/fa';
import { FaArrowTrendDown, FaArrowTrendUp } from 'react-icons/fa6';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import { MdOutlineExpandMore } from 'react-icons/md';

import { IndicatorsService } from '@/client/index.ts';
import Loader from '@/components/Loader.tsx';
import { CTag } from '@/components/common/CTag.tsx';
import { Pagination } from '@/components/common/Pagination.tsx';
import { ResultNotFound } from '@/components/common/ResultNotFound.tsx';
import { Tooltip } from '@/components/ui/tooltip';
import { Route } from '@/routes/_layout/$slug/indicators/collecte/$year/manage.tsx';

import {
  category,
  collectionFrequency,
  columnsDefManageYear,
  defaultColumnVisibility,
  trajectory,
} from '../index/IndicatorData.tsx';
import IndicatorDrawer from '../index/drawer/IndicatorDrawer.tsx';
import { ManageCollecteDeleteDialog } from './ManageCollecteDeleteDialog.tsx';
import { ManageCollecteUpdateDrawer } from './ManageCollecteUpdateDrawer.tsx';

function getIndicatorsYearSettingsQueryOptions({
  slug,
  year,
  page,
  pageSize,
  filter,
  sort,
  search,
  group_by,
}: {
  slug: string;
  year: number;
  page: number;
  pageSize: number;
  filter: string;
  sort: string;
  search: string;
  group_by: string;
}) {
  return {
    queryKey: ['indicators_year_settings', { slug, year, page, pageSize, filter, sort, search, group_by }],
    queryFn: async () => {
      if (!year) return null;
      return await IndicatorsService.readIndicatorYearSettings({
        companySlug: slug,
        year: year,
        offset: group_by !== '' ? 1 : (page - 1) * pageSize,
        limit: group_by !== '' ? 9999 : pageSize,
        filter: filter,
        sort: sort,
        search: search,
      });
    },
  };
}

export function ManageCollecteTablePerYear({ mode }: { mode: string | undefined }) {
  const navigate = useNavigate({ from: Route.fullPath });
  const { slug, year } = useParams({ strict: false });
  const { page, pageSize, filter, sort, search, group_by, columns } = Route.useSearch();
  const [indicatorDetailsId, setIndicatorDetailsId] = useState<string | null>(null);
  const [grouping, setGrouping] = useState<GroupingState>([]);
  const [count, setCount] = useState<number>(0);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const {
    data: indicators,
    isLoading,
    refetch,
  } = useQuery({
    ...getIndicatorsYearSettingsQueryOptions({
      slug,
      year,
      page,
      pageSize,
      filter,
      sort,
      search,
      group_by,
    }),
  });

  useEffect(() => {
    if (indicators && indicators.count !== undefined) {
      setCount(indicators.count);
    }
  }, [indicators]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (grouping.length === 0 && group_by !== '') setGrouping([group_by]);
    else if (grouping.length > 0 && grouping[0] !== group_by) {
      setGrouping([group_by]);
    }
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    refetch();
  }, [year, page, filter, pageSize, sort, search, group_by]);

  useEffect(() => {
    if (indicators && count < page * pageSize && page > 1) {
      navigate({
        search: (prev: Record<string, string>) => ({
          ...prev,
          page: Math.floor(count / pageSize) + 1,
        }),
      });
    }
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (year !== '') refetch();
  }, [year]);

  const columnsManage = [
    {
      accessorKey: 'indicator_name',
      baseKey: 'name',
      header: t('collection.columnsHeader.indicatorName'),
      size: 300,
      options: {
        enableSorting: true,
      },
      cell: ({ row }: { row: any }) => (
        <HStack>
          <IconButton
            variant="ghost"
            aria-label="info"
            size="2xs"
            colorPalette="gray"
            borderRadius={'full'}
            color={'gray.500'}
            _hover={{ color: 'white' }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setIndicatorDetailsId(row.original.indicator_id);
            }}
          >
            <HiOutlineInformationCircle />
          </IconButton>
          {row.original.reference_year === true && (
            <Tooltip
              showArrow
              content={t('collection.manage.columnsHeader.referenceYear')}
              contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
              openDelay={100}
              closeDelay={100}
            >
              <span style={{ cursor: 'pointer' }}>✨</span>
            </Tooltip>
          )}
          <Text fontWeight={'600'}>{row.original.indicator_name}</Text>
        </HStack>
      ),
    },
    {
      accessorKey: 'pillar_name',
      baseKey: 'indicator___commitment___pillar___name',
      header: t('collection.columnsHeader.pillarName'),
      options: {
        enableSorting: true,
        enableGrouping: true,
      },
      cell: ({ row }: { row: any }) =>
        row.original.pillar_name ? (
          <CTag label={row.original.pillar_name ? row.original.pillar_name : 'N/R'} labelToColor={true} />
        ) : (
          <Text color="gray.500" textStyle="sm">
            N/R
          </Text>
        ),
    },
    {
      accessorKey: 'commitment_name',
      baseKey: 'indicator___commitment___name',
      header: t('collection.columnsHeader.commitmentName'),
      options: {
        enableSorting: true,
        enableHiding: true,
        enableGrouping: true,
      },
      cell: ({ row }: { row: any }) => {
        return (
          row.original.commitment_name || (
            <Text color="gray.500" textStyle="sm">
              N/R
            </Text>
          )
        );
      },
    },
    {
      accessorKey: 'category',
      baseKey: 'category',
      header: t('collection.columnsHeader.categoryName'),
      size: 80,
      options: {
        enableSorting: true,
        enableGrouping: true,
      },
      cell: ({ row }: { row: any }) => {
        const isComposite = row.original.category === 'composite';
        const asSimpleIndicator = row.original.composite_as_simple_indicator;

        return (
          <CTag
            label={
              (category.items.find((item) => item.value === row.original.category)?.label || '') +
              (isComposite && asSimpleIndicator ? ' (as simple)' : '')
            }
            bg={'primary.50'}
          />
        );
      },
    },
    {
      accessorKey: 'unit',
      baseKey: 'unit',
      header: t('collection.columnsHeader.unit'),
      size: 80,
      options: {
        enableSorting: true,
        enableGrouping: true,
      },
    },
    {
      accessorKey: 'data_collection_frequency',
      baseKey: 'indicator_year_setting___data_collection_frequency',
      header: t('collection.columnsHeader.collectionFrequency'),
      options: {
        enableSorting: true,
        enableGrouping: true,
      },
      cell: ({ row }: { row: any }) => {
        const dataCollectionFrequency = collectionFrequency.items.find(
          (item) => item.value === row.original.data_collection_frequency
        );

        return (
          dataCollectionFrequency?.label || (
            <Text color="gray.500" textStyle="sm">
              N/R
            </Text>
          )
        );
      },
    },
    {
      accessorKey: 'trajectory',
      baseKey: 'indicator_year_setting___trajectory',
      header: t('collection.columnsHeader.trajectory'),
      size: 80,
      options: {
        enableSorting: true,
        enableGrouping: true,
      },
      cell: ({ row }: { row: any }) => {
        if (row.original.trajectory === null)
          return (
            <Text color="gray.500" textStyle="sm">
              N/R
            </Text>
          );
        const label = trajectory.items.find((item) => item.value === row.original.trajectory)?.label;

        return (
          <>
            {row.original.trajectory === 'decrease' && (
              <HStack color="orange.500">
                <Icon size="md">
                  <FaArrowTrendDown />
                </Icon>
                {label}
              </HStack>
            )}
            {row.original.trajectory === 'steady' && (
              <HStack color="yellow.500">
                <Icon size="md">
                  <FaLongArrowAltRight />
                </Icon>
                {label}
              </HStack>
            )}
            {row.original.trajectory === 'increase' && (
              <HStack color="green.500">
                <Icon size="md">
                  <FaArrowTrendUp />
                </Icon>
                {label}
              </HStack>
            )}
          </>
        );
      },
    },
    {
      accessorKey: 'objective',
      baseKey: 'indicator_year_setting___objective',
      header: t('collection.columnsHeader.objective'),
      size: 100,
      cell: ({ row }: { row: any }) => {
        return (
          row.original.objective?.toString() || (
            <Text color="gray.500" textStyle="sm">
              N/R
            </Text>
          )
        );
      },
    },
    {
      accessorKey: 'consolidated',
      baseKey: 'indicator_year_setting___consolidated',
      header: t('collection.columnsHeader.collectedByEntity'),
      size: 140,
      options: {
        enableSorting: true,
        enableGrouping: true,
      },
      cell: ({ row }: { row: any }) => {
        if (row.original.data_collection_frequency == null) {
          return (
            <Text color="gray.500" textStyle="sm">
              N/R
            </Text>
          );
        }
        return (
          <Center>
            <CTag
              label={
                !row.original.consolidated ? (
                  <HStack>
                    <FaCheck />
                    Oui
                  </HStack>
                ) : (
                  <HStack>
                    <FaTimes />
                    Non
                  </HStack>
                )
              }
              bg={!row.original.consolidated ? 'primary.100' : 'red.500'}
              color={!row.original.consolidated ? 'primary.900' : 'white'}
            />
          </Center>
        );
      },
    },
  ];

  const data = useMemo(() => indicators?.data ?? [], [indicators?.data]);

  const parsedColumns = useMemo(() => {
    if (columns === '') return defaultColumnVisibility;
    const visibleColumns = new Set(columns.split(','));
    visibleColumns.add(group_by);

    return columnsDefManageYear.reduce<Record<string, boolean>>((acc, column) => {
      acc[column.accessorKey] = visibleColumns.has(column.accessorKey);
      return acc;
    }, {});
  }, [columns, group_by]);
  const table = useReactTable({
    data,
    // @ts-ignore
    columns: columnsManage,
    initialState: {
      grouping: [],
    },
    state: {
      columnVisibility: parsedColumns,
      grouping: grouping,
    },
    manualSorting: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const [selection, setSelection] = useState<string[]>([]);
  const [lastSelected, setLastSelected] = useState<string | null>(null);

  const handleClick = (id: string, event: React.MouseEvent) => {
    if (event.shiftKey && lastSelected !== null) {
      window.getSelection()?.removeAllRanges();
      const lastIndex = table.getRowModel().rows.findIndex((item) => item.id === lastSelected);
      const currentIndex = table.getRowModel().rows.findIndex((item) => item.id === id);
      const [start, end] = [lastIndex, currentIndex].sort((a, b) => a - b);

      const range = table
        .getRowModel()
        .rows.slice(start, end + 1)
        .map((item) => item.id);
      setSelection((prev) => Array.from(new Set([...prev, ...range])));
    } else {
      if ((window.getSelection()?.focusOffset ?? 0) < 1) {
        setSelection((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
      }
    }
    setLastSelected(id);
  };

  const hasSelection = selection.length > 0;
  const indeterminate = hasSelection && selection.length < table.getRowModel().rows.length;

  const canDelete =
    hasSelection &&
    table
      .getRowModel()
      .rows.filter((row) => selection.includes(row.id))
      .some((row) => row.original.data_collection_frequency != null);

  const handleDelete = async () => {
    await IndicatorsService.deleteIndicatorYearSettings({
      companySlug: slug,
      year: year,
      requestBody: table
        .getRowModel()
        .rows.filter((row) => selection.includes(row.id))
        .map((row) => row.original.indicator_id),
    });
    refetch();
    setSelection([]);
  };

  const isPaginated =
    count !== 0 && count > pageSize && indicators?.data.length !== undefined && indicators?.data.length < count;

  return (
    <>
      {isLoading ? (
        <Loader loadingText={t('loading.indicators.manage')} />
      ) : (
        <>
          <Box maxH={'calc(95vh - 176px)'} borderColor={'transparent'}>
            <Table.ScrollArea
              maxH={'calc(95vh - 176px)'}
              whiteSpace={'break-spaces'}
              mx={4}
              borderWidth={1}
              ref={scrollAreaRef}
              borderColor={'transparent'}
            >
              <Table.Root size="md" interactive w={'100%'} stickyHeader borderColor={'transparent'}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Table.Header key={headerGroup.id} borderColor={'transparent'}>
                    <Table.Row bg={'#F1F7FA'}>
                      <Table.ColumnHeader w="6">
                        <Center>
                          <Checkbox.Root
                            aria-label="Select all rows"
                            bg={{ base: 'gray.50', _dark: 'gray.700' }}
                            checked={indeterminate ? 'indeterminate' : selection.length > 0}
                            onCheckedChange={(changes) => {
                              setSelection(changes.checked ? table.getRowModel().rows.map((row) => row.id) : []);
                            }}
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control borderRadius={'5px'} borderColor="gray.200" />
                          </Checkbox.Root>
                        </Center>
                      </Table.ColumnHeader>
                      {headerGroup.headers.map((header) => (
                        <Table.ColumnHeader
                          key={header.id}
                          style={{ width: `${header.getSize()}px` }}
                          alignContent={'end'}
                          placeContent={'center'}
                        >
                          <Box display={'flex'} flexDir={'column'}>
                            <Box color={{ base: 'gray.600', _dark: 'white' }} fontWeight={'semibold'} display={'flex'}>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </Box>
                          </Box>
                        </Table.ColumnHeader>
                      ))}
                    </Table.Row>
                  </Table.Header>
                ))}
                <Table.Body>
                  {table.getRowModel().rows.map((row) => (
                    <Table.Row key={row.id} data-selected={selection.includes(row.id) ? '' : undefined}>
                      <Table.Cell
                        p={4}
                        bgColor={selection.includes(row.id) ? '#F9FFFE' : ''}
                        borderRadius={'5px 0 0 5px'}
                        border={selection.includes(row.id) ? '2px solid #269584' : ''}
                        borderRight={selection.includes(row.id) ? 'none' : ''}
                        borderColor={selection.includes(row.id) ? 'none' : ''}
                      >
                        {!row.getIsGrouped() && (
                          <Center>
                            <Checkbox.Root
                              aria-label="Select row"
                              checked={selection.includes(row.id)}
                              onClick={(e) => {
                                e.preventDefault();
                                handleClick(row.id, e);
                              }}
                            >
                              <Checkbox.HiddenInput />
                              <Checkbox.Control
                                borderRadius={'5px'}
                                bg={selection.includes(row.id) ? '#269584' : 'transparent'}
                                borderColor="gray.200"
                              />
                            </Checkbox.Root>
                          </Center>
                        )}
                      </Table.Cell>

                      {row.getVisibleCells().map((cell) => (
                        <Table.Cell
                          key={cell.id}
                          borderRadius={'5px 0 0 5px'}
                          border={selection.includes(row.id) ? '2px solid red' : ''}
                          borderLeft={selection.includes(row.id) ? 'none' : ''}
                          borderRight={selection.includes(row.id) ? 'none' : ''}
                          borderColor={selection.includes(row.id) ? '#269584' : ''}
                          bgColor={selection.includes(row.id) ? '#F9FFFE' : ''}
                        >
                          {cell.getIsGrouped() ? (
                            <Box onClick={() => row.toggleExpanded()}>
                              <HStack>
                                <Icon
                                  size={'md'}
                                  rotate={row.getIsExpanded() ? '0deg' : '-90deg'}
                                  transition={'rotate 0.2s ease-in-out'}
                                >
                                  <MdOutlineExpandMore />
                                </Icon>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}{' '}
                                <CTag
                                  label={`${row.subRows.length}`}
                                  color={'primary.50'}
                                  bg={'primary.600'}
                                  ml={'auto'}
                                />
                              </HStack>
                            </Box>
                          ) : cell.getIsAggregated() ? (
                            flexRender(
                              cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
                              cell.getContext()
                            )
                          ) : cell.getIsPlaceholder() ? null : (
                            flexRender(cell.column.columnDef.cell, cell.getContext())
                          )}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Table.ScrollArea>
          </Box>
          <Box>
            <Pagination isPaginated={isPaginated} count={count} page={page} pageSize={pageSize} navigate={navigate} />
          </Box>
          <IndicatorDrawer
            indicatorId={indicatorDetailsId ?? undefined}
            isOpen={indicatorDetailsId !== null}
            setIsOpen={(state: boolean) => setIndicatorDetailsId(state ? indicatorDetailsId : null)}
            editDisabled={true}
          />
          {count === 0 && filter !== '' && sort !== '' && <ResultNotFound />}
          {count === 0 && filter === '' && sort === '' && (
            <AbsoluteCenter>
              <EmptyState.Root>
                <EmptyState.Content>
                  <VStack textAlign="center">
                    <EmptyState.Title>{t('messages.hints.addIndicator.title')}</EmptyState.Title>
                    <EmptyState.Description>{t('messages.hints.addFirstIndicatorFromManage')}</EmptyState.Description>
                  </VStack>
                </EmptyState.Content>
              </EmptyState.Root>
            </AbsoluteCenter>
          )}
        </>
      )}
      <ActionBar.Root open={hasSelection}>
        <ActionBar.Positioner>
          <ActionBar.Content>
            <ActionBar.SelectionTrigger>
              {selection.length} {t('collection.selected')}
            </ActionBar.SelectionTrigger>
            <ActionBar.Separator />
            <ManageCollecteUpdateDrawer
              year={year}
              selectedIndicators={table
                .getRowModel()
                .rows.filter((row) => selection.includes(row.id))
                .map((row) => row.original)}
              onUpdate={() => {
                refetch();
                setSelection([]);
              }}
              mode={mode}
            />
            {canDelete && (
              <>
                <ActionBar.Separator />
                <ManageCollecteDeleteDialog onDelete={handleDelete} />
              </>
            )}
          </ActionBar.Content>
        </ActionBar.Positioner>
      </ActionBar.Root>
    </>
  );
}

export default ManageCollecteTablePerYear;
